/* eslint-disable @typescript-eslint/promise-function-async */
import { broker } from '#~/agent/broker.js';
import { useIsEmbeddedBridgeOnly } from '#~/agent/hooks.js';
import type { Proxy } from '#~/agent/types.js';
import { hasOnlyEmbeddedCameras } from '#~/agent/utils.js';
import {
  DESKTOP_ONLY_FEATURE,
  EVENT_CATEGORIES,
  EVENT_NAMES,
  useAnalytics,
  VIDEO_MODES_PROPERTIES
} from '#~/analytics/index.js';
import { reportDesktopOnlyFeatureGate } from '#~/analytics/domains/feature-gate.js';
import { limitCanUseCloudMode } from '#~/api/billing/limits.js';
import { toIdFromHex } from '#~/api/utils.js';
import { useDepot } from '#~/depot/hooks.js';
import { getElectronUrl, isElectronApp } from '#~/electron/utils.js';
import { broker as lkBroker } from '#~/livekit/broker.js';
import { nats } from '#~/nats/index.js';
import { clear, post } from '#~/notifications/index.js';
import { useLastRoomIdPref } from '#~/room/hooks.js';
import { Paths } from '#~/routes/constants.js';
import { Anchor } from '@studio/design-system/button';
import { Icon } from '@studio/design-system/icon';
import { usePrompt } from '@studio/design-system/prompt';
import { Archetype as Switch } from '@studio/design-system/switch';
import { vendorName } from '@studio/design-system/themes/vendor.js';
import { normalizeId } from '@studio/models';
import type { Room as M_Room } from '@studio/models/room';
import { ConnectionMode } from '@tryiris-ai/common.protojs/agent/v1/agent';
import { AgentRegistryMembershipRole } from '@tryiris-ai/common.protojs/api/v1/agent_registry';
import { clsx } from 'cn';
import type { ReactElement } from 'react';
import { useEffect, useRef, useState } from 'react';
import { twMerge } from 'cn';
import { shallow } from 'zustand/shallow';
import { DataSTag, Sel, STagNew } from '#~/stag/tag.js';

interface Props {
  className?: string;
  disabled?: boolean;
}

const dirtyState = {
  lastManuallyChangedProtocolAt: 0,
  wasLocked: false,
  showingProtocolChangedPrompt: false,
  protocolRestored: false,
  autoLocalSwitchoverOccurred: false,
  showingLocalModeLockedPrompt: false,
  // Guards the PROGRAMMATIC close below. prompt.hide() is NOT passive:
  // dialog.tsx:73 fires onOpenChange, prompt.tsx routes that to the cancel
  // handler, and prompt/hooks.ts wired cancel -> onConfirm(false). Without
  // this flag, closing a stale modal would run the admin Unlock path.
  suppressLockedPromptConfirm: false,
  loadedAt: new Date()
};

let localModeSwitchTimetout: NodeJS.Timeout | null = null;

// How long a manual local-mode switch may take to open the local connection
// before it is declared failed and the toggle falls back to cloud.
const LOCAL_CONNECT_TIMEOUT_MS = 15_000;

/** Local mode switch. */
export const LocalModeSwitch = ({ className, disabled }: Props): ReactElement => {
  const agents: Proxy[] = [];
  const { track } = useAnalytics();

  const isEmbeddedBridgeOnly = useIsEmbeddedBridgeOnly();

  for (const proxy of broker) {
    if (proxy) {
      agents.push(proxy);
    }
  }

  // Embedded cameras stream over the Cloud only. With no desktop-bridge source beside them,
  // Local Mode has nothing to carry, so the toggle and the padlock refuse it with an alert.
  // Mixed Studios keep Local for their desktop sources.
  const embeddedCamerasOnly = hasOnlyEmbeddedCameras(agents);

  const searchParams = new URLSearchParams(location.search);
  const code = searchParams.get('code');

  const prompt = usePrompt();
  const [checkedOverride, setCheckedOverride] = useState<boolean | undefined>(undefined);

  const showLocalModeUnavailable = (): void => {
    prompt.confirm({
      title: 'Local Mode Unavailable',
      className: 'min-w-[430px]',
      message: (
        <div className='px-20 text-sm font-medium'>
          <div style={{ lineHeight: '18px' }}>
            {vendorName()}-Enabled devices currently operate in Cloud Mode only. To use Local Mode,
            remove these devices and reconnect them over your local network.
            <br />
            <br />
            Local Mode support for {vendorName()}-Enabled devices is coming soon.
          </div>
        </div>
      ),
      cancel: undefined,
      okay: {
        label: 'Cancel'
      },
      onConfirm: () => {
        // Just close the modal, no action needed
      }
    });
  };

  // `agents` is rebuilt every render, so the livekit poll below reads it
  // through a ref instead of listing it as an effect dependency (which tore
  // the 500ms interval down and recreated it on every render).
  const agentsRef = useRef(agents);
  agentsRef.current = agents;

  // agent.state updates on state.tx; this component otherwise only
  // re-renders from local hooks / a 500ms livekit poll that no-ops when
  // currentConnectionMode is unchanged. Without this, a padlock mutate can
  // land (connectionMode LOCAL in 11ms) while the icon stays on the stale
  // unlock snapshot (ENG-4665).
  const [, setStateTxTick] = useState(0);
  const agentIdsKey = agents.map((agent) => agent.id).join(',');
  useEffect(() => {
    const onTx = (): void => {
      setStateTxTick((tick) => tick + 1);
    };
    const attached = agentsRef.current.filter(Boolean);
    for (const agent of attached) {
      agent.on('state.tx', onTx);
    }
    return () => {
      for (const agent of attached) {
        agent.off('state.tx', onTx);
      }
    };
  }, [agentIdsKey]);

  const setCheckedOverrideWithTimeout = (value: boolean): void => {
    if (localModeSwitchTimetout) {
      clearTimeout(localModeSwitchTimetout);
    }

    setCheckedOverride(value);
    localModeSwitchTimetout = setTimeout(() => {
      setCheckedOverride(undefined);
    }, 30_000);
  };

  const [currentConnectionMode, setCurrentConnectionMode] = useState(
    ConnectionMode.CONNECTION_MODE_UNKNOWN
  );

  const [silent, setSilent] = useState(true);
  const [videoReconnecting, setVideoReconnecting] = useState(false);

  const setMode = async (mode: 'cloud' | 'local', opts?: { auto?: boolean }): Promise<void> => {
    if (Date.now() - dirtyState.loadedAt.getTime() > 10_000 && !silent) {
      post(
        {
          title: `Attempting to connect to ${mode === 'cloud' ? 'Cloud' : 'Local'} Mode...`,
          message: 'Your sources are reconnecting. This may take a few moments.',
          severity: 'info',
          id: 'pending-mode-switch-notification'
        },
        { autoClose: false }
      );
    }

    // Session policy is sticky on this fan-out only. T4 per-agent
    // LiveKitImpl.setMode('cloud') must not write it.
    const policy = mode === 'local' ? 'deny' : 'allow';
    for (const livekit of lkBroker) {
      livekit.setCloudVideoPolicy(policy);
    }

    const results = await Promise.allSettled(
      agents.map(async (agent) => {
        if (!agent) {
          return { agentId: null, success: false, error: 'Agent not found' };
        }
        if (agent.embedded && mode !== 'cloud') {
          return {
            agentId: agent.id,
            success: false,
            error: 'Embedded agents cannot use local mode'
          };
        }

        try {
          const id = toIdFromHex(agent.id)!;
          await Promise.all(
            Array.from(lkBroker).map(async (livekit) => {
              await livekit.setMode(mode, id, true, opts?.auto ?? false);
            })
          );
          return { agentId: agent.id, success: true, name: agent.name || 'Unnamed Agent' };
        } catch (e) {
          return {
            agentId: agent.id,
            success: false,
            error: (e as Error).message,
            name: agent.name || 'Unnamed Agent'
          };
        }
      })
    );

    clear('pending-mode-switch-notification');

    for (const agent of agents) {
      agent?.forcePing();
    }

    setCurrentConnectionMode(
      mode === 'cloud' ? ConnectionMode.CONNECTION_MODE_CLOUD : ConnectionMode.CONNECTION_MODE_LOCAL
    );

    if (silent) {
      return;
    }

    const modeCapitalized = mode === 'cloud' ? 'Cloud' : 'Local';

    if (mode === 'cloud') {
      const allSucceeded = results.every((r) => r.status === 'fulfilled' && r.value.success);
      if (allSucceeded) {
        post({
          title: 'Cloud Mode is active',
          message: 'Sources will now route through the cloud.',
          severity: 'success'
        });
      } else {
        results.forEach((result) => {
          if (result.status === 'rejected' || !result.value.success) {
            post({
              actionable: true,
              id: 'cloud-mode-failure',
              title: 'Cloud Mode connection failed',
              message: 'Ensure you have an active internet connection with sufficient bandwidth.',
              severity: 'error'
            });
          }
        });
      }
    } else {
      // Local mode: post per-agent notifications
      results.forEach((result) => {
        if (result.status === 'fulfilled' && result.value.success) {
          post({
            title: `${modeCapitalized} Mode activated on ${result.value.name}`,
            message: 'Sources for this bridge will now route over your Local network.',
            severity: 'success'
          });
        } else {
          post(
            {
              actionable: true,
              title: `${modeCapitalized} Mode could not activate on ${(result as any).value.name}`,
              message: `Sources for ${(result as any).value.name} will route over Cloud Mode. To activate Local Mode ensure you’re connected to the same local network.`,
              severity: 'warning'
            },
            {
              autoClose: 10_000
            }
          );
        }
      });
    }
  };

  const attemptToSwitchToCloudMode = async (): Promise<void> => {
    setSilent(false);

    // Track toggling to cloud mode
    track(EVENT_NAMES.TOGGLE_CLOUD_MODE, EVENT_CATEGORIES.VIDEO_MODES, {
      [VIDEO_MODES_PROPERTIES.MODE]: 'cloud',
      [VIDEO_MODES_PROPERTIES.PREVIOUS_MODE]: 'local',
      [VIDEO_MODES_PROPERTIES.AGENT_IDS]: agents.map((agent) => agent.id)
    });

    await setMode('cloud');
    setCheckedOverride(undefined);
  };

  const attemptToSwitchToLocalMode = async (): Promise<void> => {
    setSilent(false);

    // Track toggling to local mode
    track(EVENT_NAMES.TOGGLE_LOCAL_MODE, EVENT_CATEGORIES.VIDEO_MODES, {
      [VIDEO_MODES_PROPERTIES.MODE]: 'local',
      [VIDEO_MODES_PROPERTIES.PREVIOUS_MODE]: 'cloud',
      [VIDEO_MODES_PROPERTIES.AGENT_IDS]: agents.map((agent) => agent.id)
    });

    await setMode('local');

    // Engineer req 2026-08-25: the toggle shows the indeterminate spinner
    // until the local connection is actually OPEN (setMode only records the
    // transport intent). If no local room connects within 15s, surface the
    // failure and fall back to cloud mode instead of leaving a dead toggle.
    const deadline = Date.now() + LOCAL_CONNECT_TIMEOUT_MS;
    const localAgents = agents.filter((agent) => agent && !agent.embedded);

    const connected =
      localAgents.length > 0 &&
      (await new Promise<boolean>((resolve) => {
        const timer = setInterval(() => {
          const allConnected = localAgents.every((agent) =>
            Array.from(lkBroker).every((livekit) =>
              livekit.isLocalRoomConnected(toIdFromHex(agent.id)!)
            )
          );
          if (allConnected) {
            clearInterval(timer);
            resolve(true);
          } else if (Date.now() > deadline) {
            clearInterval(timer);
            resolve(false);
          }
        }, 500);
      }));

    if (!connected) {
      post(
        {
          actionable: true,
          title: 'Local Mode connection failed',
          message:
            'Could not establish a local connection within 15 seconds. Switching back to Cloud Mode — ensure you are on the same local network as the bridge.',
          severity: 'error'
        },
        { autoClose: 10_000 }
      );
      await attemptToSwitchToCloudMode();
      return;
    }

    setCheckedOverride(undefined);
  };

  const { isLocalModeCapable } = nats;
  const checked =
    (checkedOverride === undefined && !isLocalModeCapable) ||
    (checkedOverride ?? currentConnectionMode === ConnectionMode.CONNECTION_MODE_CLOUD);

  const updateProtocol = (nextValue: boolean): void => {
    if (!isElectronApp()) {
      // ENG-4332: this branch is the product's own guidance modal ("open the desktop
      // app to use Local Mode"). Recording it makes the guidance measurable — how many
      // browser users want Local Mode — without ever going through an error path.
      reportDesktopOnlyFeatureGate(DESKTOP_ONLY_FEATURE.LOCAL_MODE);
      prompt.confirm({
        title: 'Local Mode Unavailable',
        message: `Local Mode is only supported when using the native ${vendorName()} application. Please open the ${vendorName()} application to use Local Mode, or you can continue using Cloud Mode in your browser.`,
        onConfirm: (answer) => {
          if (answer) {
            window.open(`${window.location.origin}/agent/setup`, '_blank');
          }
        },
        okay: {
          label: `Open ${vendorName()} App`
        },
        className: 'pb-24',
        footNotes: (
          <div className='mt-24'>
            <a target='_blank' href={`/${Paths.agent.setup.select}`}>
              <Anchor className='text-primary'>Need to download {vendorName()}? Click here</Anchor>
            </a>
          </div>
        )
      });
      return;
    }

    if (!nextValue && embeddedCamerasOnly) {
      showLocalModeUnavailable();
      return;
    }

    if (nextValue) {
      prompt.confirm({
        title: 'Enable Cloud Mode?',
        className: 'min-w-[425px]',
        message: (
          <div className='px-28 font-regular'>
            <div style={{ lineHeight: '21px' }}>
              Cloud Mode routes your video via the Cloud to your Studio, so you can use it from
              anywhere in the world.
            </div>
          </div>
        ),
        okay: {
          label: 'Enable Cloud Mode'
        },
        onConfirm(answer) {
          if (answer) {
            dirtyState.lastManuallyChangedProtocolAt = Date.now();
            setCheckedOverrideWithTimeout(true);
            attemptToSwitchToCloudMode();
          }
        }
      });
    } else {
      prompt.confirm({
        title: 'Enable Local Mode?',
        className: 'min-w-[425px]',
        message: (
          <div className='px-28 font-regular'>
            <div style={{ lineHeight: '21px' }}>
              Local mode uses only your local network to ingest video. Your sources will no longer
              be streamed from the Cloud. {vendorName()}-enabled sources have no local video path,
              so they keep streaming over the Cloud.
            </div>
          </div>
        ),
        cancel: {
          label: 'Cancel'
        },
        okay: {
          label: 'Enable Local Mode'
        },
        onConfirm(answer) {
          if (answer) {
            dirtyState.lastManuallyChangedProtocolAt = Date.now();
            setCheckedOverrideWithTimeout(false);
            attemptToSwitchToLocalMode();
          }
        }
      });
    }
  };

  const lockedStateFromState = agents.some((agent) => {
    return agent?.state.control?.encoder?.connectionMode === ConnectionMode.CONNECTION_MODE_LOCAL;
  });

  const isBillingLock = agents.some((agent) => {
    return agent?.state.control?.encoder?.billingLocked;
  });

  // Locked = explicit admin lock (connectionMode LOCAL) OR billing
  // enforcement (billingLocked). blockCloudUsage no longer writes
  // connectionMode, so billing must contribute here to keep locking clients.
  const lockedState = lockedStateFromState || isBillingLock;

  useEffect(() => {
    Array.from(lkBroker).map((livekit) => {
      for (const agent of agents) {
        if (!agent) {
          continue;
        }
        livekit.setLockedLocal(agent.id!, lockedState);
      }
    });
  }, [lockedState]);

  const setLockedState = (value: boolean): void => {
    if (!limitCanUseCloudMode()) {
      value = true;
    }

    // Track locking local mode
    track(EVENT_NAMES.LOCK_LOCAL_MODE, EVENT_CATEGORIES.VIDEO_MODES, {
      [VIDEO_MODES_PROPERTIES.IS_LOCKED]: value,
      [VIDEO_MODES_PROPERTIES.AGENT_IDS]: agents.map((agent) => agent.id)
    });

    Array.from(lkBroker).forEach((livekit) => {
      for (const agent of agents) {
        if (!agent?.id) continue;
        livekit.setLockedLocal(agent.id, value);
      }
    });

    for (const agent of agents) {
      const billingOverride = agent?.state.control?.encoder?.billingLocked;
      const toggle = billingOverride ? false : value;

      agent?.mutate(
        {
          control: {
            encoder: {
              connectionMode: toggle
                ? ConnectionMode.CONNECTION_MODE_LOCAL
                : ConnectionMode.CONNECTION_MODE_CLOUD_AND_LOCAL
            }
          }
        },
        {},
        false
      );
    }
  };

  const isLocked = lockedState;

  useEffect(() => {
    const syncFromLivekit = (): void => {
      let reconnecting = false;
      let anyLocal = false;
      let anyCloud = false;
      for (const livekit of lkBroker) {
        for (const agent of agentsRef.current) {
          if (!agent) continue;
          const id = toIdFromHex(agent.id)!;
          if (livekit.isVideoReconnecting(id)) {
            reconnecting = true;
          }
          const agentMode = livekit.modeForAgent(id);
          if (agentMode === 'local') {
            anyLocal = true;
          } else if (agentMode === 'cloud') {
            anyCloud = true;
          }
        }
      }
      setVideoReconnecting(reconnecting);
      // LOCAL WINS. This replaced a last-agent-wins loop whose result depended
      // on broker iteration order, and it makes this poll agree with
      // lockedStateFromState, which decides the session is local via
      // `.some(... CONNECTION_MODE_LOCAL)`. Two readings of one thing should not
      // be able to contradict each other.
      //
      // CORRECTED RATIONALE (was wrong in the original commit message and in an
      // earlier version of this comment; see report sections R and V). The
      // original story was that last-agent-wins across THREE agents flipped the
      // session to CLOUD and opened the 'Studio Locked in Local Mode' prompt.
      // That is FALSE: every run, including the one that motivated the change,
      // had exactly ONE agent — CI #1509 32681343411 logged a single id under
      // all seven worker pids. The multi-agent mechanism never applied anywhere
      // and this change does NOT explain that prompt, which is still open.
      //
      // What IS established: bandwidth-governor :105 and :184 go green with this
      // on hardware (run 32691487432, artifact-verified), and order-independence
      // is correct on its own merits. Nothing more is claimed.
      // Per rule: verify-and-escalate (ai.knowledge/global).

      if (anyLocal) {
        setCurrentConnectionMode(ConnectionMode.CONNECTION_MODE_LOCAL);
      } else if (anyCloud) {
        setCurrentConnectionMode(ConnectionMode.CONNECTION_MODE_CLOUD);
      }
    };

    const interval = setInterval(syncFromLivekit, 500);
    syncFromLivekit();
    return () => clearInterval(interval);
  }, []);

  const [rooms] = useDepot((µ) => [µ.roomRecords, µ.produce], shallow);

  const [lastRoomID, _] = useLastRoomIdPref();
  const studioRoomID = normalizeId<M_Room['id']>(lastRoomID);

  const role = rooms[studioRoomID!]?.permissions?.role;

  const isAdmin =
    role &&
    [
      AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_ADMIN,
      AgentRegistryMembershipRole.AGENT_REGISTRY_MEMBERSHIP_ROLE_OWNER
    ].includes(role);

  const effectiveChecked = checked;
  const effectiveDisabled = disabled || isLocked;

  const [isLocalModeStarting, setIsLocalModeStarting] = useState(true);
  const [localModeConnectionCheckStartedAt] = useState(() => Date.now());

  // Spinner-in-toggle covers every transitional state (engineer req
  // 2026-08-25): pending mode switch, video reconnecting, and local mode
  // still connecting — no side text.
  let pending =
    (isLocked && checked) ||
    checkedOverride !== undefined ||
    (!disabled && videoReconnecting) ||
    (isLocalModeStarting && !effectiveChecked);

  useEffect(() => {
    const fn = () => {
      const isPreStarting = Date.now() - localModeConnectionCheckStartedAt < 1_000;
      let isLocalModeConnecting = false;

      for (const livekit of lkBroker) {
        for (const agent of broker) {
          try {
            if (livekit?.isStartingLocalMode(toIdFromHex(agent.id)!)) {
              isLocalModeConnecting = true;
            }
          } catch (e) {
            console.error('Error checking local mode connection:', e);
          }
        }
      }

      setIsLocalModeStarting(isPreStarting || isLocalModeConnecting);

      if (!isPreStarting && !isLocalModeConnecting) {
        clearInterval(interval);
      }
    };

    const interval = setInterval(fn, 1000);

    fn();

    return () => {
      clearInterval(interval);
    };
  }, [isLocalModeStarting, localModeConnectionCheckStartedAt, setIsLocalModeStarting]);

  useEffect(() => {
    if (checked && isLocked) {
      if (isLocalModeStarting) {
        return;
      }

      if (dirtyState.showingLocalModeLockedPrompt) {
        return;
      }

      dirtyState.showingLocalModeLockedPrompt = true;

      if (!limitCanUseCloudMode() || isBillingLock) {
        if (dirtyState.showingLocalModeLockedPrompt) {
          prompt.hide();
          dirtyState.showingLocalModeLockedPrompt = false;
        }
        return;
      }

      // Get names of agents locked to local mode
      const lockedAgents = agents
        .filter(
          (agent) =>
            agent?.state.control?.encoder?.connectionMode === ConnectionMode.CONNECTION_MODE_LOCAL
        )
        .map((agent) => agent?.name || 'Unnamed Agent');

      const messageContent = (
        <div className='px-28 font-regular'>
          <div className='mb-[20px]' style={{ lineHeight: '21px' }}>
            An Admin has locked this Studio in Local Mode for the following bridges:{' '}
            {lockedAgents.join(', ')}. To access the Studio, ensure you're connected to the same
            local network as these bridges.
          </div>
        </div>
      );

      if (isElectronApp()) {
        prompt.confirm({
          title: 'Studio Locked in Local Mode',
          className: 'min-w-[524px]',
          message: messageContent,
          cancel: isAdmin ? { label: 'Unlock Local Mode', variant: 'danger' } : undefined,
          okay: { label: 'Connect to Local Mode' },
          responseRequired: true,
          onConfirm(answer) {
            // Programmatic close (condition cleared) must not perform the
            // Unlock action. Pure early return — it must NOT touch the flag;
            // the cleanup branch owns arming and clearing it.
            if (dirtyState.suppressLockedPromptConfirm) {
              return;
            }
            if (!isAdmin && !answer) {
              return;
            }
            if (!answer) {
              setLockedState(false);
              attemptToSwitchToCloudMode();
              dirtyState.showingLocalModeLockedPrompt = false;
              return;
            }
            dirtyState.lastManuallyChangedProtocolAt = Date.now();
            setCheckedOverrideWithTimeout(false);
            attemptToSwitchToLocalMode();
            dirtyState.showingLocalModeLockedPrompt = false;
          }
        });
      } else {
        prompt.confirm({
          title: 'Studio Locked in Local Mode',
          className: 'min-w-[524px]',
          message: messageContent,
          cancel: isAdmin ? { label: 'Unlock Local Mode', variant: 'danger' } : undefined,
          okay: { label: `Open ${vendorName()} App` },
          onConfirm: (answer) => {
            // Programmatic close (condition cleared) must not perform the
            // Unlock action. Pure early return — it must NOT touch the flag;
            // the cleanup branch owns arming and clearing it.
            if (dirtyState.suppressLockedPromptConfirm) {
              return;
            }
            if (answer) {
              window.open(getElectronUrl(`code/${code}`), '_self');
              return;
            }
            if (!isAdmin) {
              return;
            }
            setLockedState(false);
            attemptToSwitchToCloudMode();
            dirtyState.showingLocalModeLockedPrompt = false;
          },
          responseRequired: true,
          footNotes: (
            <div className='mt-24'>
              <a target='_blank' href={`/${Paths.agent.setup.select}`}>
                <Anchor className='text-primary'>
                  Need to download {vendorName()}? Click here
                </Anchor>
              </a>
            </div>
          )
        });
      }
    } else if (dirtyState.showingLocalModeLockedPrompt) {
      // CLOSE-ON-CONDITION-CLEARED. Without this branch the prompt opens on
      // `checked && isLocked` and NOTHING ever closes it for the normal case:
      // the hide at the top of this effect is reachable only on the
      // billing/limit path, and the other hide lives in the isLocked-transition
      // effect below, whose body is guarded by `isLocked != wasLocked` with the
      // hide in its else — so it fires only on isLocked TRUE -> FALSE. The
      // transition we actually observe is `checked` flipping false while
      // isLocked STAYS true, which had no closer at all.
      //
      // Because responseRequired disables Escape and outside-click, the overlay
      // could not lapse and a test could not dismiss it, so it outlived its own
      // trigger and kept intercepting clicks. CI #1509 run 32695728558 measured
      // the leak directly — 4 gate evaluations across mac+win with
      // willOpen=false while showingPrompt was still true — and on mac the
      // prompt opened 4x during iris-enabled (which PASSED, 0 intercepts) and
      // was still up 6 specs later to block bandwidth-governor :184 (0 gates in
      // its window, 3 overlay intercepts).
      //
      // Correctness does not depend on the CI story: a modal asserting "An Admin
      // has locked this Studio in Local Mode" while that no longer holds is
      // wrong for a human operator too. Closing it is an operator-visible
      // behaviour change and is called out in the PR description.
      // Per rule: verify-and-escalate (ai.knowledge/global) — the leak is
      // measured, not inferred.
      // prompt.hide() is NOT a passive dismissal — verified by trace, not assumed:
      //   prompt/hooks.ts:47 hide -> dialog/hooks.ts:42 close -> dialog.tsx:31
      //   close -> dialog.tsx:73 `current.onOpenChange?.(nextOpen)` -> prompt.tsx
      //   handleOpenChange -> (choice ? onOkay : onCancel)() -> prompt/hooks.ts:19-35
      //   wired cancel -> onConfirm(false).
      // `choice` is only set by the okay button, so a programmatic close always
      // takes the CANCEL path, which on an admin session runs setLockedState(false)
      // + attemptToSwitchToCloudMode(). Cleanup must not perform an admin unlock,
      // so suppress the callback across the hide.
      // The reset is placed immediately after hide() rather than inside
      // onConfirm. dialog.tsx:73 `current.onOpenChange?.(nextOpen)` is invoked
      // synchronously INLINE by #handleOpened before it returns, so once hide() returns the
      // callback has either already run or never will — clearing here therefore
      // cannot latch the flag on. Clearing inside onConfirm WOULD latch it on
      // the non-admin path, PERMANENTLY — and that is a fact, not a worry:
      // switch.tsx passes `cancel: isAdmin ? {...} : undefined`, and
      // deepmerge({cancel:{onClick}}, {cancel: undefined}) yields
      // cancel === undefined, so on a non-admin session the wired handler does
      // NOT survive the merge and onConfirm NEVER FIRES there. A clear placed
      // inside onConfirm would therefore never run on that path, the flag would
      // stay armed forever, and the next genuine admin Cancel would be silently
      // swallowed. On an admin session the handler DOES survive, which is why
      // the suppression is needed at all. Both halves are pinned by the
      // behavioural test 'deepmerge: explicit cancel:undefined kills the wired
      // onConfirm (non-admin)' in local-cloud-video-policy.test.ts, run against
      // the real deepmerge package.
      //
      // AND THE POINT THAT OUTLIVES BOTH FACTS: it is the SHAPE that makes this
      // safe, not the merge semantics. With clear-after-hide, BOTH merge
      // outcomes are harmless — if the handler survives, onConfirm fires and the
      // guard catches it; if it dies, nothing fires at all. The test records
      // which branch reality currently takes; the shape is why that no longer
      // matters. So a future change to deepmerge, to `cancel`'s props, or to
      // isAdmin gating must not be read as license to move the clear back inside
      // onConfirm — that would re-arm the hazard silently.
      // hide(force) is not an alternative: `force` only bypasses onBeforeClose,
      // it does not suppress onOpenChange.
      dirtyState.suppressLockedPromptConfirm = true;
      prompt.hide();
      dirtyState.suppressLockedPromptConfirm = false;
      dirtyState.showingLocalModeLockedPrompt = false;
    }
  }, [isAdmin, isLocked, checked, prompt, isLocalModeStarting]);

  useEffect(() => {
    if (dirtyState.autoLocalSwitchoverOccurred) {
      return;
    }

    if (!isLocalModeCapable) {
      return;
    }

    dirtyState.autoLocalSwitchoverOccurred = true;

    // Embedded studios default to Cloud (LiveKitImpl holds their desktop bridges);
    // fanning local out here would override that and deny cloud video.
    if (Array.from(lkBroker).some((livekit) => livekit.holdsCloudByDefault())) {
      return;
    }

    if (!dirtyState.showingLocalModeLockedPrompt) {
      // auto: this one-shot switchover is machinery — it must defer to a
      // standing operator cloud request instead of clobbering it
      // (CI #1509 run 32904567960; see LiveKitImpl.setMode).
      setMode('local', { auto: true });
    }
  }, [isLocalModeCapable]);

  useEffect(() => {
    if (isLocked != dirtyState.wasLocked) {
      dirtyState.wasLocked = isLocked;

      if (isLocked) {
        if (checked) {
          return;
        }

        post({
          title: 'Local Mode is locked',
          message:
            'Your Studio is now locked in Local Mode. Users are now required to access the Studio in Local Mode.',
          severity: 'info'
        });
      } else {
        post({
          title: 'Local Mode is unlocked',
          message:
            'Your Studio is unlocked from Local Mode. Users can now use the Studio in Local or Cloud mode.',
          severity: 'info'
        });

        if (dirtyState.showingLocalModeLockedPrompt) {
          prompt.hide();
          dirtyState.showingLocalModeLockedPrompt = false;
        }
      }
    }
  }, [isLocked, checked, prompt]);

  const connectionModeTag =
    currentConnectionMode === ConnectionMode.CONNECTION_MODE_CLOUD ? 'cloud' : 'local';

  return (
    <div
      className={twMerge(
        clsx(className, 'flex items-center', {
          'pointer-events-none opacity-50': disabled || isEmbeddedBridgeOnly
        })
      )}
      {...STagNew(Sel.LocalMode, 'switch')}
    >
      <span
        className='flex items-center'
        {...(disabled ? {} : DataSTag(Sel.LocalMode, 'connection-mode', connectionModeTag))}
      >
        {!effectiveChecked && (isLocked || isAdmin) && (
          <div
            onClick={
              isAdmin
                ? () => {
                    // Locking would cut the Embedded cameras' Cloud video with nothing gained.
                    // Unlocking stays available.
                    if (!isLocked && embeddedCamerasOnly) {
                      showLocalModeUnavailable();
                      return;
                    }
                    setLockedState(!isLocked);
                  }
                : () => {}
            }
            className={twMerge(
              'mr-8 flex items-center justify-center rounded-full',
              ((isLocked && !isAdmin) || !limitCanUseCloudMode()) && 'opacity-[0.5]',
              isAdmin && 'cursor-pointer'
            )}
          >
            <Icon className={isLocked ? 'text-primary' : ''} icon={isLocked ? 'lock' : 'unlock'} />
          </div>
        )}

        <Switch
          checked={!isLocked && effectiveChecked}
          disabled={effectiveDisabled}
          className='w-48'
          offLabel={
            pending ? (
              <Icon className='animate-spin font-bold' icon='spinner' />
            ) : (
              <Icon className='mt-4 font-bold' icon='cloud-dash' />
            )
          }
          onCheckedChange={updateProtocol}
          onLabel={
            pending ? (
              <Icon className='animate-spin font-bold' icon='spinner' />
            ) : (
              <Icon className='mt-4 font-bold' icon='cloud' />
            )
          }
          thumbClassName='w-[21px]'
        />
      </span>
      {/* videoReconnecting renders as the in-toggle spinner (pending) —
          keep the selector for e2e without visible side text. */}
      {!disabled && videoReconnecting ? (
        <span {...DataSTag(Sel.LocalMode, 'video-reconnecting', 'true')} className='hidden' />
      ) : null}
    </div>
  );
};
